import { PORTAL_LOGIN_CONFIG_PATH, PortalLoginConfig } from '@willow/types-iso';

import { SET_PASSWORD_RESET_REDIRECT } from './authRoutes';

// Relative on purpose: every call has to stay same-origin on whichever tenant hostname the
// borrower is on, which is what the server's same-origin check requires.
const PORTAL_AUTH_API_BASE = '/api/portal/auth';

const PORTAL_AUTH_ERROR_CODES = [
  'INVALID_EMAIL_OR_PASSWORD',
  'INVALID_EMAIL',
  'INVALID_TOKEN',
  'USER_NOT_FOUND',
  'PASSWORD_TOO_SHORT',
  'PASSWORD_TOO_LONG',
  'PASSWORD_COMPROMISED',
  'INVALID_ORIGIN',
  'RATE_LIMITED',
  'NETWORK',
  'UNKNOWN',
] as const;

export type PortalAuthErrorCode = (typeof PORTAL_AUTH_ERROR_CODES)[number];

export type PortalAuthResult =
  | { ok: true }
  | { ok: false; code: PortalAuthErrorCode; status?: number; message?: string };

const isPortalAuthErrorCode = (value: string): value is PortalAuthErrorCode =>
  (PORTAL_AUTH_ERROR_CODES as readonly string[]).includes(value);

const readErrorBody = async (response: Response): Promise<{ code?: string; message?: string }> => {
  try {
    const body: unknown = await response.json();
    if (typeof body !== 'object' || body === null) {
      return {};
    }
    return {
      code: 'code' in body && typeof body.code === 'string' ? body.code : undefined,
      message: 'message' in body && typeof body.message === 'string' ? body.message : undefined,
    };
  } catch {
    return {};
  }
};

const post = async (path: string, body: Record<string, unknown>): Promise<PortalAuthResult> => {
  let response: Response;
  try {
    response = await fetch(`${PORTAL_AUTH_API_BASE}${path}`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  } catch {
    return { ok: false, code: 'NETWORK' };
  }

  if (response.ok) {
    return { ok: true };
  }
  if (response.status === 429) {
    return { ok: false, code: 'RATE_LIMITED', status: response.status };
  }

  const { code: rawCode, message } = await readErrorBody(response);
  const code = rawCode && isPortalAuthErrorCode(rawCode) ? rawCode : 'UNKNOWN';
  // Only the password messages are written for a borrower to read. Every other server message
  // could leak whether an account exists, so the screens supply their own copy.
  const safeMessage = code.startsWith('PASSWORD_') ? message : undefined;
  return { ok: false, code, status: response.status, message: safeMessage };
};

export const fetchLoginConfig = async (): Promise<PortalLoginConfig> => {
  const response = await fetch(PORTAL_LOGIN_CONFIG_PATH, {
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) {
    throw new Error(`Portal login config responded with status ${response.status}`);
  }
  return PortalLoginConfig.parse(await response.json());
};

export const signIn = (input: { email: string; password: string }): Promise<PortalAuthResult> =>
  post('/sign-in/email', input);

export const requestPasswordReset = (input: { email: string }): Promise<PortalAuthResult> =>
  post('/request-password-reset', { email: input.email, redirectTo: SET_PASSWORD_RESET_REDIRECT });

export const resetPassword = (input: { token: string; newPassword: string }): Promise<PortalAuthResult> =>
  post('/reset-password', input);

export const acceptInvite = (input: { token: string; email: string; newPassword: string }): Promise<PortalAuthResult> =>
  post('/accept-invite', input);

export const signOut = (): Promise<PortalAuthResult> => post('/sign-out', {});
